import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { twoFactorApi, type TrustedDevice } from "@/lib/api";
import { Spinner } from "@/components/ui/spinner";
import {
  ShieldCheck,
  ShieldOff,
  QrCode,
  Key,
  Smartphone,
  Trash2,
  RefreshCw,
  Copy,
  Check,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Laptop,
  Monitor,
} from "lucide-react";

export function TwoFactorSettings() {
  const queryClient = useQueryClient();
  const [showSetup, setShowSetup] = useState(false);
  const [setupStep, setSetupStep] = useState<"qr" | "verify" | "backup">("qr");
  const [setupData, setSetupData] = useState<{
    qrCodeUrl: string;
    secret: string;
    backupCodes: string[];
  } | null>(null);
  const [verifyCode, setVerifyCode] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [copiedBackupCodes, setCopiedBackupCodes] = useState(false);
  const [showDevices, setShowDevices] = useState(false);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);

  // Fetch 2FA status
  const { data: status, isLoading: statusLoading } = useQuery({
    queryKey: ["2fa-status"],
    queryFn: async () => {
      const response = await twoFactorApi.getStatus();
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error("Failed to fetch 2FA status");
    },
  });

  // Fetch trusted devices
  const { data: devicesData, isLoading: devicesLoading } = useQuery({
    queryKey: ["trusted-devices"],
    queryFn: async () => {
      const response = await twoFactorApi.getTrustedDevices();
      if (response.success && response.data) {
        return response.data.devices;
      }
      throw new Error("Failed to fetch trusted devices");
    },
    enabled: status?.enabled === true,
  });

  // Setup mutation
  const setupMutation = useMutation({
    mutationFn: async () => {
      const response = await twoFactorApi.setup();
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.error?.message || "Setup failed");
    },
    onSuccess: (data) => {
      setSetupData(data);
      setSetupStep("qr");
      setShowSetup(true);
      setError(null);
    },
    onError: (error: Error) => {
      setError(error.message);
    },
  });

  // Verify setup mutation
  const verifyMutation = useMutation({
    mutationFn: async (code: string) => {
      const response = await twoFactorApi.verifySetup(code);
      if (response.success) {
        return response.data;
      }
      throw new Error(response.error?.message || "Verification failed");
    },
    onSuccess: () => {
      setSetupStep("backup");
      setError(null);
      queryClient.invalidateQueries({ queryKey: ["2fa-status"] });
    },
    onError: (error: Error) => {
      setError(error.message);
      setVerifyCode("");
    },
  });

  // Disable mutation
  const disableMutation = useMutation({
    mutationFn: async (password: string) => {
      const response = await twoFactorApi.disable(password);
      if (response.success) {
        return response.data;
      }
      throw new Error(response.error?.message || "Failed to disable 2FA");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["2fa-status"] });
      queryClient.invalidateQueries({ queryKey: ["trusted-devices"] });
      setShowDisableConfirm(false);
      setPassword("");
      setError(null);
    },
    onError: (error: Error) => {
      setError(error.message);
    },
  });

  // Regenerate backup codes mutation
  const regenerateMutation = useMutation({
    mutationFn: async (password: string) => {
      const response = await twoFactorApi.regenerateBackupCodes(password);
      if (response.success && response.data) {
        return response.data.backupCodes;
      }
      throw new Error(response.error?.message || "Failed to regenerate codes");
    },
    onSuccess: (codes) => {
      setSetupData((prev) => prev ? { ...prev, backupCodes: codes } : null);
      if (!setupData) {
        setSetupData({
          qrCodeUrl: "",
          secret: "",
          backupCodes: codes,
        });
      }
      setSetupStep("backup");
      setShowSetup(true);
      setShowRegenerateConfirm(false);
      setPassword("");
      setError(null);
      queryClient.invalidateQueries({ queryKey: ["2fa-status"] });
    },
    onError: (error: Error) => {
      setError(error.message);
    },
  });

  // Remove device mutation
  const removeDeviceMutation = useMutation({
    mutationFn: async (deviceId: string) => {
      const response = await twoFactorApi.removeTrustedDevice(deviceId);
      if (response.success) {
        return response.data;
      }
      throw new Error(response.error?.message || "Failed to remove device");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trusted-devices"] });
    },
  });

  // Remove all devices mutation
  const removeAllDevicesMutation = useMutation({
    mutationFn: async () => {
      const response = await twoFactorApi.removeAllTrustedDevices();
      if (response.success) {
        return response.data;
      }
      throw new Error(response.error?.message || "Failed to remove devices");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trusted-devices"] });
    },
  });

  const copyToClipboard = async (text: string, type: "secret" | "backup") => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === "secret") {
        setCopiedSecret(true);
        setTimeout(() => setCopiedSecret(false), 2000);
      } else {
        setCopiedBackupCodes(true);
        setTimeout(() => setCopiedBackupCodes(false), 2000);
      }
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    }
  };

  const handleStartSetup = () => {
    setError(null);
    setupMutation.mutate();
  };

  const handleVerify = () => {
    if (verifyCode.length !== 6) {
      setError("Koden måste vara 6 siffror");
      return;
    }
    setError(null);
    verifyMutation.mutate(verifyCode);
  };

  const handleFinishSetup = () => {
    setShowSetup(false);
    setSetupData(null);
    setSetupStep("qr");
    setVerifyCode("");
  };

  const handleDisable = () => {
    if (!password) {
      setError("Lösenord krävs");
      return;
    }
    setError(null);
    disableMutation.mutate(password);
  };

  const handleRegenerate = () => {
    if (!password) {
      setError("Lösenord krävs");
      return;
    }
    setError(null);
    regenerateMutation.mutate(password);
  };

  const getDeviceIcon = (device: TrustedDevice) => {
    const os = device.os?.toLowerCase() || "";
    if (os.includes("windows") || os.includes("mac") || os.includes("linux")) {
      return <Monitor className="w-5 h-5" />;
    }
    if (os.includes("android") || os.includes("ios")) {
      return <Smartphone className="w-5 h-5" />;
    }
    return <Laptop className="w-5 h-5" />;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("sv-SE", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (statusLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status Card */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center ${
                status?.enabled
                  ? "bg-green-100 text-green-600"
                  : "bg-gray-100 text-gray-500"
              }`}
            >
              {status?.enabled ? (
                <ShieldCheck className="w-6 h-6" />
              ) : (
                <ShieldOff className="w-6 h-6" />
              )}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Tvåfaktorsautentisering (2FA)
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                {status?.enabled
                  ? "Ditt konto är skyddat med tvåfaktorsautentisering."
                  : "Lägg till ett extra säkerhetslager på ditt konto."}
              </p>
              {status?.enabled && status.enabledAt && (
                <p className="text-xs text-gray-400 mt-2">
                  Aktiverad {formatDate(status.enabledAt)}
                </p>
              )}
            </div>
          </div>
          <div
            className={`px-3 py-1 rounded-full text-sm font-medium ${
              status?.enabled
                ? "bg-green-100 text-green-700"
                : "bg-gray-100 text-gray-600"
            }`}
          >
            {status?.enabled ? "Aktiverad" : "Inaktiverad"}
          </div>
        </div>

        {/* Actions */}
        <div className="mt-6 flex flex-wrap gap-3">
          {!status?.enabled ? (
            <button
              onClick={handleStartSetup}
              disabled={setupMutation.isPending}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700
                disabled:bg-blue-300 disabled:cursor-not-allowed transition-colors
                flex items-center gap-2"
            >
              {setupMutation.isPending ? (
                <Spinner size="sm" />
              ) : (
                <QrCode className="w-4 h-4" />
              )}
              Aktivera 2FA
            </button>
          ) : (
            <>
              <button
                onClick={() => setShowRegenerateConfirm(true)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200
                  transition-colors flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Nya backup-koder
                {status.backupCodesRemaining <= 3 && (
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
                    {status.backupCodesRemaining} kvar
                  </span>
                )}
              </button>
              <button
                onClick={() => setShowDisableConfirm(true)}
                className="px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100
                  transition-colors flex items-center gap-2"
              >
                <ShieldOff className="w-4 h-4" />
                Inaktivera 2FA
              </button>
            </>
          )}
        </div>
      </div>

      {/* Setup Modal */}
      {showSetup && setupData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={handleFinishSetup}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Setup Step: QR Code */}
              {setupStep === "qr" && (
                <>
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <QrCode className="w-8 h-8 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">
                      Skanna QR-koden
                    </h3>
                    <p className="text-gray-500 mt-2">
                      Öppna din autentiseringsapp och skanna koden nedan.
                    </p>
                  </div>

                  {/* QR Code */}
                  <div className="flex justify-center mb-6">
                    <div className="p-4 bg-white border-2 border-gray-200 rounded-xl">
                      <img
                        src={setupData.qrCodeUrl}
                        alt="QR Code"
                        className="w-48 h-48"
                      />
                    </div>
                  </div>

                  {/* Manual Entry */}
                  <div className="bg-gray-50 rounded-lg p-4 mb-6">
                    <p className="text-sm text-gray-600 mb-2">
                      Eller ange koden manuellt:
                    </p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 px-3 py-2 bg-white border border-gray-200 rounded-lg font-mono text-sm break-all">
                        {setupData.secret}
                      </code>
                      <button
                        onClick={() => copyToClipboard(setupData.secret, "secret")}
                        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                      >
                        {copiedSecret ? (
                          <Check className="w-5 h-5 text-green-500" />
                        ) : (
                          <Copy className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>

                  <button
                    onClick={() => setSetupStep("verify")}
                    className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Jag har skannat koden
                  </button>
                </>
              )}

              {/* Setup Step: Verify */}
              {setupStep === "verify" && (
                <>
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Key className="w-8 h-8 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">
                      Verifiera inställningen
                    </h3>
                    <p className="text-gray-500 mt-2">
                      Ange den 6-siffriga koden från din autentiseringsapp.
                    </p>
                  </div>

                  {error && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-100 rounded-lg flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  )}

                  <div className="mb-6">
                    <input
                      type="text"
                      value={verifyCode}
                      onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder="000000"
                      className="w-full px-4 py-3 text-center text-2xl font-mono border border-gray-300 rounded-lg
                        focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
                      maxLength={6}
                    />
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => setSetupStep("qr")}
                      className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Tillbaka
                    </button>
                    <button
                      onClick={handleVerify}
                      disabled={verifyMutation.isPending || verifyCode.length !== 6}
                      className="flex-1 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700
                        disabled:bg-blue-300 disabled:cursor-not-allowed transition-colors
                        flex items-center justify-center gap-2"
                    >
                      {verifyMutation.isPending ? <Spinner size="sm" /> : "Verifiera"}
                    </button>
                  </div>
                </>
              )}

              {/* Setup Step: Backup Codes */}
              {setupStep === "backup" && (
                <>
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Check className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">
                      2FA aktiverad!
                    </h3>
                    <p className="text-gray-500 mt-2">
                      Spara dessa backup-koder på ett säkert ställe. Varje kod kan
                      endast användas en gång.
                    </p>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-amber-700">
                        <p className="font-medium">Viktigt!</p>
                        <p>
                          Om du förlorar din telefon kan du använda dessa koder
                          för att logga in. Spara dem säkert!
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <div className="grid grid-cols-2 gap-2">
                      {setupData.backupCodes.map((code, index) => (
                        <div
                          key={index}
                          className="px-3 py-2 bg-white border border-gray-200 rounded-lg font-mono text-center"
                        >
                          {code}
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={() =>
                        copyToClipboard(setupData.backupCodes.join("\n"), "backup")
                      }
                      className="mt-3 w-full py-2 text-sm text-gray-600 hover:text-gray-800
                        flex items-center justify-center gap-2"
                    >
                      {copiedBackupCodes ? (
                        <>
                          <Check className="w-4 h-4 text-green-500" />
                          Kopierade!
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          Kopiera alla koder
                        </>
                      )}
                    </button>
                  </div>

                  <button
                    onClick={handleFinishSetup}
                    className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Jag har sparat koderna
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Disable Confirmation Modal */}
      {showDisableConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => {
              setShowDisableConfirm(false);
              setPassword("");
              setError(null);
            }}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShieldOff className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900">
                Inaktivera 2FA?
              </h3>
              <p className="text-gray-500 mt-2">
                Ditt konto blir mindre säkert utan tvåfaktorsautentisering. Ange
                ditt lösenord för att bekräfta.
              </p>
            </div>

            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-100 rounded-lg flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Lösenord
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Ange ditt lösenord"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg
                  focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDisableConfirm(false);
                  setPassword("");
                  setError(null);
                }}
                className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={handleDisable}
                disabled={disableMutation.isPending || !password}
                className="flex-1 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700
                  disabled:bg-red-300 disabled:cursor-not-allowed transition-colors
                  flex items-center justify-center gap-2"
              >
                {disableMutation.isPending ? <Spinner size="sm" /> : "Inaktivera"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Regenerate Backup Codes Modal */}
      {showRegenerateConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => {
              setShowRegenerateConfirm(false);
              setPassword("");
              setError(null);
            }}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <RefreshCw className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900">
                Nya backup-koder
              </h3>
              <p className="text-gray-500 mt-2">
                Dina gamla backup-koder kommer att ogiltigförklaras. Ange ditt
                lösenord för att generera nya koder.
              </p>
            </div>

            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-100 rounded-lg flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Lösenord
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Ange ditt lösenord"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg
                  focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowRegenerateConfirm(false);
                  setPassword("");
                  setError(null);
                }}
                className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={handleRegenerate}
                disabled={regenerateMutation.isPending || !password}
                className="flex-1 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700
                  disabled:bg-blue-300 disabled:cursor-not-allowed transition-colors
                  flex items-center justify-center gap-2"
              >
                {regenerateMutation.isPending ? <Spinner size="sm" /> : "Generera nya"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Trusted Devices Section */}
      {status?.enabled && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <button
            onClick={() => setShowDevices(!showDevices)}
            className="w-full flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <Smartphone className="w-5 h-5 text-gray-500" />
              <div className="text-left">
                <h3 className="font-semibold text-gray-900">Betrodda enheter</h3>
                <p className="text-sm text-gray-500">
                  {devicesData?.length || 0} enheter sparade
                </p>
              </div>
            </div>
            {showDevices ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>

          {showDevices && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              {devicesLoading ? (
                <div className="flex justify-center py-4">
                  <Spinner />
                </div>
              ) : devicesData && devicesData.length > 0 ? (
                <>
                  <div className="space-y-3">
                    {devicesData.map((device) => (
                      <div
                        key={device.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className="text-gray-500">{getDeviceIcon(device)}</div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {device.deviceName || device.browser || "Okänd enhet"}
                            </p>
                            <p className="text-xs text-gray-500">
                              {device.os} • {device.browser}
                              {device.ipAddress && ` • ${device.ipAddress}`}
                            </p>
                            <p className="text-xs text-gray-400">
                              Senast använd: {formatDate(device.lastUsedAt)}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => removeDeviceMutation.mutate(device.id)}
                          disabled={removeDeviceMutation.isPending}
                          className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => removeAllDevicesMutation.mutate()}
                    disabled={removeAllDevicesMutation.isPending}
                    className="mt-4 w-full py-2 text-sm text-red-600 hover:text-red-700
                      flex items-center justify-center gap-2"
                  >
                    {removeAllDevicesMutation.isPending ? (
                      <Spinner size="sm" />
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4" />
                        Ta bort alla betrodda enheter
                      </>
                    )}
                  </button>
                </>
              ) : (
                <p className="text-center text-gray-500 py-4">
                  Inga betrodda enheter sparade ännu.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
